const DASHBOARD_BASE_URL =
  (import.meta.env.VITE_DASHBOARD_URL as string) ??
  "https://dashboard.rockethealth.app";

export const MENS_HEALTH_TCONSULT_STANDARD_ID =
  "20edfa48-12e6-4cf0-9b60-7bd936820881";
export const MENS_HEALTH_CONSULT_STANDARD_ID =
  "83a6b867-e983-4229-b4ff-62ddfa287c60";
export const MENS_HEALTH_PAID_FOLLOW_UP_STANDARD_ID =
  "3d2e2325-147c-4cb4-aed0-d4a13965cb87";

export const MENS_HEALTH_BUNDLE_IDS = [
  MENS_HEALTH_TCONSULT_STANDARD_ID,
  MENS_HEALTH_PAID_FOLLOW_UP_STANDARD_ID,
  MENS_HEALTH_CONSULT_STANDARD_ID,
];

type MensHealthSession = {
  name?: string | null;
  start_time?: string | Date | null;
  status?: string | null;
  is_unsuccessful?: boolean | null;
};

const isActiveSession = (status?: string | null) =>
  !status || status.toLowerCase() === "active";

// A session counts toward scheduling decisions only when it is active (not
// cancelled) and was not marked unsuccessful. Marking a session unsuccessful
// releases its bundle slot server-side and treats it as if it never happened,
// so the scheduler must ignore it too — otherwise an unsuccessful therapy
// session hides the T-consult therapy link, leaving no way to re-book it.
const isCountableSession = (session: MensHealthSession) =>
  isActiveSession(session.status) && !session.is_unsuccessful;

const isIndiaCountry = (country?: string | null) =>
  !country || country.toLowerCase() === "india";

const buildLink = (path: string, usageId: string, includeUsageId = true) =>
  `${DASHBOARD_BASE_URL}${path}${includeUsageId ? `?usage_id=${usageId}` : ""}`;

export const isMensHealthBundle = (standardId: string) =>
  MENS_HEALTH_BUNDLE_IDS.includes(standardId);

export const isMensHealthTconsultBundle = (standardId: string) =>
  standardId === MENS_HEALTH_TCONSULT_STANDARD_ID;

const getActiveTherapySessions = (sessions: MensHealthSession[] = []) =>
  sessions.filter(
    (session) =>
      isCountableSession(session) &&
      session.name?.toLowerCase().includes("therapy"),
  );

export function getMensHealthFollowUpLinks({
  usage_id,
  user_country,
}: {
  usage_id: string;
  user_country?: string | null;
}) {
  const isIndia = isIndiaCountry(user_country ?? undefined);
  const freePath = isIndia
    ? "/schedule/free-followup"
    : "/schedule/free-followup-int";
  const paidPath = isIndia
    ? "/schedule/follow-up"
    : "/schedule/follow-up-paid-int";

  return {
    free: buildLink(freePath, usage_id),
    paid: buildLink(paidPath, usage_id, false),
  };
}

export function getMensHealthTherapyLink({
  standard_id,
  usage_id,
  sessions,
}: {
  standard_id: string;
  usage_id: string;
  sessions?: MensHealthSession[];
}) {
  if (!isMensHealthTconsultBundle(standard_id)) return "";
  const therapySessions = getActiveTherapySessions(sessions);
  if (therapySessions.length > 0) return "";
  return buildLink("/schedule/menstherapy", usage_id);
}
